import React from 'react'
import { Field, FormikProps } from 'formik'
import { RichTextInput } from '../../../../../../../components/inputs/RichTextInput/RichTextInput'
import { FormSectionTwoColumns } from '../../../../../../../components/forms/FormSectionTwoColumns'
import { CheckboxInput } from '../../../../../../../components/inputs/CheckboxInput'
import { InfoPopover } from '../../../../../../../components/InfoPopover'

interface Props {
  formik: FormikProps<any>
}

export const TechnicalFormB1Nv2: React.FC<Props> = ({ formik }) => {
  return (
    <FormSectionTwoColumns
      title="n) základní požadavky na předčasné užívání staveb a zkušební provoz staveb"
      subtitle="doba jejich trvání ve vztahu k dokončení a užívání stavby"
      sectionKey="b1n"
    >
      <div className="sm:col-span-6 mt-5 flex flex-col gap-3">
        <div>
          <div className="flex">
            <Field
              name="b1n.pretermUsage.enabled"
              component={CheckboxInput}
              label="Požadavky na předčasné užívání staveb"
            />
            <InfoPopover
              content={
                <div className="text-left">
                  <p>
                    <b>Požadavky na předčasné užívání staveb</b> lze očekávat v situacích, kdy má
                    být stavba využívána dříve, než je formálně dokončena, zkolaudována a uvedena do
                    plného provozu. Taková potřeba může vzniknout z různých důvodů:
                  </p>
                  <ol className="list-decimal pl-5 mt-3 font-bold">
                    <li>
                      Postupná realizace stavebních etap
                      <ul className="list-disc pl-5 font-normal">
                        <li>
                          Stavba je navržena k etapové realizaci, kde některé části mohou být
                          dokončeny a užívány dříve než zbytek.
                        </li>
                        <li>
                          Například obytné části domu jsou dokončeny, ale garáže, zahrada nebo
                          vedlejší stavby (bazén, terasa) teprve čekají na dokončení.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Časová naléhavost užívání
                      <ul className="list-disc pl-5 font-normal">
                        <li>
                          Tlak na zahájení užívání stavby z důvodu časové tísně, například před
                          stěhováním nebo zahájením provozu v daném objektu.
                        </li>
                        <li>
                          Např. rodina se musí nastěhovat do domu před kolaudací, protože končí
                          nájemní smlouva v jejich stávajícím bydlení.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Potřeba provozního ověření
                      <ul className="list-disc pl-5 font-normal">
                        <li>
                          Před zahájením plného provozu je někdy třeba zkušební užívání některých
                          částí stavby (např. technických zařízení).
                        </li>
                        <li>
                          Příkladem může být uvedení topného systému, kanalizace nebo elektrického
                          vedení do provozu.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Nutnost využití nekompletní infrastruktury
                      <ul className="list-disc pl-5 font-normal">
                        <li>
                          Pokud jsou dokončeny základní funkční části (např. připojení na vodovod,
                          kanalizaci a elektřinu), ale jiné části (např. zahradní úpravy nebo
                          sekundární budovy) čekají na realizaci.
                        </li>
                        <li>
                          Například v developerských projektech, kde se byty předávají uživatelům,
                          zatímco společné prostory ještě nejsou dokončeny.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Zpoždění dokončení stavby
                      <ul className="list-disc pl-5 font-normal">
                        <li>
                          Pokud zpoždění stavebních prací ohrožuje využitelnost objektu, lze požádat
                          o předčasné užívání.
                        </li>
                        <li>
                          Například dokončení interiérů probíhá dle plánu, ale exteriérové práce se
                          opožďují kvůli špatnému počasí.
                        </li>
                      </ul>
                    </li>
                  </ol>
                  <p className="mt-5">
                    <b>Poznámka:</b> Předčasné užívání stavby vždy podléhá souhlasu příslušného{' '}
                    <b>stavebního úřadu</b> a musí být splněny minimální požadavky na bezpečnost a
                    funkčnost, aby byla stavba způsobilá k dočasnému užívání.
                  </p>
                </div>
              }
            />
          </div>
          {formik.values?.b1n?.pretermUsage?.enabled && (
            <div className="mt-2 pl-7 flex flex-col gap-3">
              <Field
                name="b1n.pretermUsage.description"
                component={RichTextInput}
                label="Popis požadavků"
                aiHint="Požadavky na předčasné užívání staveb - Popis požadavků"
              />
              <Field
                name="b1n.pretermUsage.length"
                component={RichTextInput}
                label="Doba trvání vzhledem k dokončení a užívání stavby"
                aiHint="Požadavky na předčasné užívání staveb - Doba trvání vzhledem k dokončení a užívání stavby"
              />
            </div>
          )}
        </div>
        <div>
          <div className="flex">
            <Field
              name="b1n.testUsage.enabled"
              component={CheckboxInput}
              label="Zkušební provoz staveb"
            />
            <InfoPopover
              content={
                <div className="text-left">
                  <p>
                    Zkušební provoz je specifický režim užívání stavby, který se využívá zejména u{' '}
                    <b>technicky složitých nebo technologicky náročných objektů</b> a zařízení.
                    Slouží k ověření, zda stavba, její vybavení a technologie splňují projektované
                    parametry a bezpečnostní požadavky.
                  </p>
                  <p className="font-bold mt-3">
                    Stavby, které typicky podléhají zkušebnímu provozu:
                  </p>
                  <ol className="list-decimal pl-5 mt-3 font-bold">
                    <li>
                      Průmyslové a výrobní objekty
                      <ul className="list-disc pl-5 font-normal">
                        <li>Továrny, výrobní haly a průmyslové komplexy.</li>
                        <li>Technologická zařízení pro výrobu (stroje, pásové systémy apod.).</li>
                        <li>
                          Např. chemické závody, kde je třeba ověřit bezpečnost procesů a zařízení.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Energetické stavby
                      <ul className="list-disc pl-5 font-normal">
                        <li>Elektrárny (jaderné, vodní, větrné, tepelné).</li>
                        <li>Přenosové a distribuční sítě (rozvodny, transformátory).</li>
                        <li>
                          Zařízení pro obnovitelné zdroje energie, jako jsou bioplynové stanice nebo
                          fotovoltaické elektrárny.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Stavby pro veřejnou dopravu a infrastrukturu
                      <ul className="list-disc pl-5 font-normal">
                        <li>Dopravní stavby: dálnice, železnice, letiště, tunely.</li>
                        <li>
                          Technologické zařízení v dopravě, např. signalizační a zabezpečovací
                          systémy.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Stavby občanské vybavenosti s technologiemi
                      <ul className="list-disc pl-5 font-normal">
                        <li>
                          Nemocnice, laboratoře a zdravotnická zařízení (pro testování lékařských
                          technologií a systémů).
                        </li>
                        <li>
                          Školy a kulturní zařízení s náročným technickým vybavením (např. divadla s
                          osvětlením a akustikou).
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Vodohospodářské stavby
                      <ul className="list-disc pl-5 font-normal">
                        <li>Čistírny odpadních vod.</li>
                        <li>Vodárenské objekty a zařízení (úpravny vody, vodojemy).</li>
                        <li>Přehrady a vodní elektrárny.</li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Komerční objekty s rozsáhlými technologiemi
                      <ul className="list-disc pl-5 font-normal">
                        <li>
                          Obchodní centra a výškové budovy s pokročilými technologickými systémy
                          (klimatizace, vzduchotechnika, evakuační systémy).
                        </li>
                        <li>
                          Datová centra, kde je nutné otestovat IT infrastrukturu a bezpečnostní
                          technologie.
                        </li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Specifické ekologické stavby
                      <ul className="list-disc pl-5 font-normal">
                        <li>Spalovny odpadů, kompostárny, recyklační centra.</li>
                        <li>Biotechnologické provozy.</li>
                      </ul>
                    </li>

                    <li className="mt-3">
                      Speciální objekty
                      <ul className="list-disc pl-5 font-normal">
                        <li>Jaderná zařízení (reaktory, sklady jaderného odpadu).</li>
                        <li>Vojenské objekty a technologie.</li>
                      </ul>
                    </li>
                  </ol>
                  <div className="font-bold mt-5">
                    Cíl zkušebního provozu:
                    <ul className="list-disc pl-10 font-normal mb-3">
                      <li>
                        Ověřit funkčnost technologií a jejich soulad s projektovou dokumentací.
                      </li>
                      <li>Prověřit bezpečnost užívání a minimalizovat rizika.</li>
                      <li>Identifikovat a odstranit případné technické nedostatky.</li>
                      <li>Předložit výsledky pro kolaudaci a povolení k trvalému užívání.</li>
                    </ul>
                    Podmínky zkušebního provozu:
                    <ul className="list-disc pl-10 font-normal">
                      <li>Povolení stavebního úřadu nebo jiného kompetentního orgánu.</li>
                      <li>Dohled odborného dozoru nebo specializovaných techniků.</li>
                      <li>Zpracování zkušebních protokolů a dokumentace.</li>
                    </ul>
                    <p className="font-normal mt-3">
                      Zkušební provoz je klíčovým krokem u staveb s vyššími nároky na technologickou
                      i bezpečnostní stránku provozu.
                    </p>
                  </div>
                </div>
              }
            />
          </div>
          {formik.values?.b1n?.testUsage?.enabled && (
            <div className="mt-2 pl-7 flex flex-col gap-3">
              <Field
                name="b1n.testUsage.description"
                component={RichTextInput}
                label="Popis požadavků"
                aiHint="Zkušební provoz staveb - Popis požadavků"
              />
              <Field
                name="b1n.testUsage.length"
                component={RichTextInput}
                label="Doba trvání vzhledem k dokončení a užívání stavby"
                aiHint="Zkušební provoz staveb - Doba trvání vzhledem k dokončení a užívání stavby"
              />
            </div>
          )}
        </div>
      </div>
    </FormSectionTwoColumns>
  )
}
